import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Grid, Chip, Alert, AlertTitle, Fade,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PsychologyIcon from '@mui/icons-material/Psychology';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SectionHeader from './SectionHeader';
import DetailSection from './DetailSection';
import TrainingLoadStatusChip from './TrainingLoadStatusChip';
import { GradientBackground } from './styled';

const TrainingLoadDetail = ({ recentContext, consistency, onBack }) => {
  const theme = useTheme();
  if (!recentContext && !consistency) return null;
  
  const {
    overall_status,
    overall_status_explanation,
    sessions_last_7_days,
    rest_days_last_7_days,
    total_volume_last_7_days,
    total_sets_last_7_days,
    accumulated_fatigue_score,
    overtraining_indicators,
    undertraining_indicators,
    session_fit,
    muscles_needing_rest,
    muscles_ready_to_train,
    muscles_overdue_for_training,
    is_newcomer,
  } = recentContext || {};

  const hasConsistency = consistency && (
    consistency.current_streak_days > 0 ||
    consistency.sessions_this_week > 0 ||
    consistency.sessions_this_month > 0
  );

  if (is_newcomer) {
    return (
      <Fade in timeout={300}>
        <Box>
          <SectionHeader icon={<PsychologyIcon />} title="Training Load & Consistency" onBack={onBack} />
          <GradientBackground color="success" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" fontWeight={700} color="success.main" gutterBottom>
              🎉 Welcome! Great job logging your workout!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {overall_status_explanation || "Keep tracking your workouts to build your training history. After a few more sessions, you'll see personalized insights."}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} sm={4}>
                <Typography variant="overline" color="text.secondary">Sessions (7d)</Typography>
                <Typography variant="h3" fontWeight={700} color="success.main">{sessions_last_7_days || 1}</Typography>
              </Grid>
              {total_sets_last_7_days > 0 && (
                <Grid item xs={6} sm={4}>
                  <Typography variant="overline" color="text.secondary">Sets (7d)</Typography>
                  <Typography variant="h3" fontWeight={700}>{total_sets_last_7_days}</Typography>
                </Grid>
              )}
              {consistency?.current_streak_days > 0 && (
                <Grid item xs={6} sm={4}>
                  <Typography variant="overline" color="text.secondary">Current Streak</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WhatshotIcon sx={{ color: 'warning.main', fontSize: 24 }} />
                    <Typography variant="h3" fontWeight={700} color="warning.main">{consistency.current_streak_days}</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </GradientBackground>
        </Box>
      </Fade>
    );
  }

  return (
    <Fade in timeout={300}>
      <Box>
        <SectionHeader icon={<PsychologyIcon />} title="Training Load & Consistency" onBack={onBack} />
        
        <GradientBackground color="primary" sx={{ p: 3, mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
              <TrainingLoadStatusChip status={overall_status} />
              {accumulated_fatigue_score > 0 && (
                <Chip
                  icon={<BatteryAlertIcon />}
                  label={`Fatigue: ${accumulated_fatigue_score.toFixed(0)}%`}
                  color={accumulated_fatigue_score > 70 ? 'error' : accumulated_fatigue_score > 40 ? 'warning' : 'success'}
                  variant="outlined"
                />
              )}
            </Box>
            {overall_status_explanation && (
              <Typography variant="body2" color="text.secondary">{overall_status_explanation}</Typography>
            )}
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <Typography variant="overline" color="text.secondary">Sessions (7d)</Typography>
              <Typography variant="h3" fontWeight={700}>{sessions_last_7_days}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="overline" color="text.secondary">Rest Days (7d)</Typography>
              <Typography variant="h3" fontWeight={700}>{rest_days_last_7_days}</Typography>
            </Grid>
            {total_sets_last_7_days > 0 && (
              <Grid item xs={6} sm={3}>
                <Typography variant="overline" color="text.secondary">Sets (7d)</Typography>
                <Typography variant="h3" fontWeight={700}>{total_sets_last_7_days}</Typography>
              </Grid>
            )}
            {total_volume_last_7_days > 0 && (
              <Grid item xs={6} sm={3}>
                <Typography variant="overline" color="text.secondary">Volume (7d)</Typography>
                <Typography variant="h3" fontWeight={700}>{(total_volume_last_7_days / 1000).toFixed(1)}k</Typography>
              </Grid>
            )}
          </Grid>
        </GradientBackground>

        {/* Consistency */}
        {hasConsistency && (
          <DetailSection title="Consistency & Streaks" icon={<WhatshotIcon sx={{ fontSize: 18 }} />} color="warning">
            <Grid container spacing={2}>
              {consistency.current_streak_days > 0 && (
                <Grid item xs={6} sm={3}>
                  <Box sx={{
                    p: 1.5,
                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                    borderRadius: 2,
                    textAlign: 'center',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                      <WhatshotIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                      <Typography variant="h4" fontWeight={800} color="warning.main">{consistency.current_streak_days}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">Day Streak</Typography>
                  </Box>
                </Grid>
              )}
              {consistency.sessions_this_week > 0 && (
                <Grid item xs={6} sm={3}>
                  <Box sx={{
                    p: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderRadius: 2,
                    textAlign: 'center',
                  }}>
                    <Typography variant="h4" fontWeight={700}>{consistency.sessions_this_week}</Typography>
                    <Typography variant="caption" color="text.secondary">This Week</Typography>
                  </Box>
                </Grid>
              )}
              {consistency.sessions_this_month > 0 && (
                <Grid item xs={6} sm={3}>
                  <Box sx={{
                    p: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderRadius: 2,
                    textAlign: 'center',
                  }}>
                    <Typography variant="h4" fontWeight={700}>{consistency.sessions_this_month}</Typography>
                    <Typography variant="caption" color="text.secondary">This Month</Typography>
                  </Box>
                </Grid>
              )}
              {consistency.average_sessions_per_week_30_day > 0 && (
                <Grid item xs={6} sm={3}>
                  <Box sx={{
                    p: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderRadius: 2,
                    textAlign: 'center',
                  }}>
                    <Typography variant="h4" fontWeight={700}>{consistency.average_sessions_per_week_30_day.toFixed(1)}</Typography>
                    <Typography variant="caption" color="text.secondary">Avg/Week (30d)</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DetailSection>
        )}

        {/* Session Fit */}
        {session_fit && (
          <DetailSection title="Session Fit" icon={<CheckCircleIcon sx={{ fontSize: 18 }} />} color="success">
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2 }}>
              <Typography variant="h4" fontWeight={700} color="success.main" sx={{ textTransform: 'capitalize' }}>
                {session_fit.rating}
              </Typography>
              <Typography variant="body1" color="text.secondary">({session_fit.rating_score?.toFixed(0)}/100)</Typography>
            </Box>
            {session_fit.explanation && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{session_fit.explanation}</Typography>}
            {session_fit.positive_aspects?.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                {session_fit.positive_aspects.map((aspect, idx) => (
                  <Typography key={idx} variant="body2" sx={{ color: 'success.main', mb: 0.5 }}>✓ {aspect}</Typography>
                ))}
              </Box>
            )}
            {session_fit.concerns?.length > 0 && (
              <Box>
                {session_fit.concerns.map((concern, idx) => (
                  <Typography key={idx} variant="body2" sx={{ color: 'warning.main', mb: 0.5 }}>⚠ {concern}</Typography>
                ))}
              </Box>
            )}
          </DetailSection>
        )}

        {/* Overtraining / Undertraining Alerts */}
        {overtraining_indicators?.is_likely_overtraining && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Overtraining Risk: {overtraining_indicators.risk_level}</AlertTitle>
            {overtraining_indicators.warning_messages?.map((msg, idx) => (
              <Typography key={idx} variant="body2">• {msg}</Typography>
            ))}
          </Alert>
        )}

        {undertraining_indicators?.is_likely_undertraining && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Undertraining Detected</AlertTitle>
            {undertraining_indicators.gap_messages?.map((msg, idx) => (
              <Typography key={idx} variant="body2">• {msg}</Typography>
            ))}
          </Alert>
        )}

        {/* Muscle Readiness */}
        {(muscles_overdue_for_training?.length > 0 || muscles_ready_to_train?.length > 0 || muscles_needing_rest?.length > 0) && (
          <DetailSection title="Muscle Readiness" icon={<FitnessCenterIcon sx={{ fontSize: 18 }} />} color="info">
            {muscles_needing_rest?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Needs Rest:</Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {muscles_needing_rest.map((muscle, idx) => (
                    <Chip key={`n-${idx}`} label={muscle} variant="outlined" color="warning" size="small" sx={{ textTransform: 'capitalize' }} />
                  ))}
                </Box>
              </Box>
            )}
            {muscles_ready_to_train?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Ready to Train:</Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {muscles_ready_to_train.map((muscle, idx) => (
                    <Chip key={`r-${idx}`} label={muscle} variant="outlined" color="success" size="small" sx={{ textTransform: 'capitalize' }} />
                  ))}
                </Box>
              </Box>
            )}
            {muscles_overdue_for_training?.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Overdue for Training:</Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {muscles_overdue_for_training.map((muscle, idx) => (
                    <Chip key={`o-${idx}`} label={muscle} variant="outlined" color="info" size="small" sx={{ textTransform: 'capitalize' }} />
                  ))}
                </Box>
              </Box>
            )}
          </DetailSection>
        )}
      </Box>
    </Fade>
  );
};

TrainingLoadDetail.propTypes = {
  recentContext: PropTypes.object,
  consistency: PropTypes.object,
  onBack: PropTypes.func.isRequired,
};

export default TrainingLoadDetail;